import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts/core';
import ReactECharts, { EChartsInstance } from 'echarts-for-react';
import { format } from 'date-fns';
import { FUSION_TRENDS_TYPE, REGULAR_DATE_FORMAT } from '@/const';
import { healthStatusNoShadowColorMap, healthStatusToNumberMap, healthStatusToShadowColorMap } from '@/constants/shared';
import {
  MAINTENANCE_CHART_CONTEXT,
  MaintenanceChart,
} from '@/modules/shared/Maintenance/MaintenanceChart/MaintenanceChart';
import { useSelector } from 'react-redux';
import { maintenanceChartVisible, type RootState } from '@/store/store';
import { Icon } from '@/components/shared/display/Icon';
import { PlusCircleIcon, RefreshIcon } from '@/assets/Icons';
interface ChartProps {
  type: FUSION_TRENDS_TYPE;
  onPointClick: (e: any) => void;
  diagnostics?: any[];
  selectedComponent?: any;
  activeChartPoint?: any;
  showMaintenance?: boolean;
}

export const Chart = ({
  type,
  onPointClick,
  diagnostics,
  selectedComponent,
  activeChartPoint,
  showMaintenance = true,
}: ChartProps) => {
  const isMaintenanceChartVisible = useSelector(maintenanceChartVisible);
  const diagnosticsChartRef = useRef<EChartsInstance>(null);
  const maintenanceChartRef = useRef<EChartsInstance>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dataZoom, setDataZoom] = useState<Record<string, number>>({
    start: 0,
    end: 100,
  });
  const [chartData, setChartData] = useState<any>([]);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [isZoomActive, setIsZoomActive] = useState(false);
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);

  const getChartData = () => {
    const clonedDiagnostics = diagnostics && [...diagnostics];
    const componentDiagnostics = clonedDiagnostics?.map((item) => {
      return [
        item[0],
        [
          ...item[1].assets.flatMap((asset: any) =>
            asset.components
              .filter((component: any) => {
                if (type === FUSION_TRENDS_TYPE.Component && selectedComponent) {
                  return component.component_id === selectedComponent.component_id;
                }
                return true;
              })
              .flatMap((component: any) => {
                if (component.diagnostics) {
                  return component.diagnostics.map((diagnostic: any) => ({
                    component_name: component.component_name,
                    component_id: component.component_id,
                    component_health_status: component.health_status,
                    ...diagnostic,
                  }));
                } else if (component.subcomponents && component.subcomponents.length > 0) {
                  // Backwards compatible: handle nested subcomponents (old structure)
                  return component.subcomponents.flatMap((sub: any) =>
                    sub.diagnostics.map((diagnostic: any) => ({
                      component_name: component.component_name,
                      component_id: component.component_id,
                      component_ui_name: component.component_ui_name,
                      component_health_status: component.health_status,
                      subcomponent_id: sub.subcomponent_id,
                      subcomponent_name: sub.subcomponent_name,
                      subcomponent_ui_name: sub.subcomponent_ui_name,
                      subcomponent_health_status: sub.subcomponent_health_status,
                      ...diagnostic,
                    }))
                  );
                } else {
                  // New structure: component has no nested subcomponents, return empty
                  return [];
                }
              })
          ),
        ],
      ];
    });

    const data =
      type === FUSION_TRENDS_TYPE.Component
        ? componentDiagnostics?.map((item) => ({
            name: format(new Date(item[0]), 'yyyy-MM-dd'),
            value: [item[0], healthStatusToNumberMap[item[1][0]?.component_health_status] ?? 5],
            clicked: false,
          })) || []
        : clonedDiagnostics?.map((item) => ({
            name: format(new Date(item[0]), 'yyyy-MM-dd'),
            value: [item[0], healthStatusToNumberMap[item?.[1]?.assets?.[0]?.health_status] ?? 5],
            clicked: false,
          })) || [];

    return data;
  };

  const toolbox = {
    left: 'right',
    itemSize: 12,
    top: -7,
    feature: {
      dataZoom: {
        title: { zoom: 'Zoom', back: '' },
        icon: { zoom: 'black', back: 'blank' },
        yAxisIndex: 'none',
        emphasis: { iconStyle: { borderColor: '#7071f3' } },
        brushStyle: { borderWidth: 1, borderColor: '#7071f3' },
      },
      restore: {
        icon: 'blank',
        emphasis: { iconStyle: { borderColor: '#7071f3' } },
      },
    },
  };

  const commonOptions = {
    height: 22,
    grid: { top: 0, right: 20, bottom: 0, left: 24 },
    animation: false,
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisTick: false,
      splitLine: { show: false },
      axisLine: { show: true },
      showMinLabel: true,
      min: chartData[0]?.value?.[0],
      max: chartData.length === 1 ? chartData[0]?.value?.[0] : undefined,
      axisLabel: {
        fontSize: 10,
        rotate: 0,
        showMaxLabel: true,
        formatter: (value: string, index: number) => {
          const date = new Date(value);
          return index === 0
            ? format(date, 'MMM dd yyyy') // First tick: with year
            : format(date, 'MMM dd'); // Others: no year
        },
      },
    },
    yAxis: {
      show: false,
      min: 0,
      max: 0.01,
    },
    // tooltip: {
    //   trigger: 'item',
    //   formatter: function (params: any) {
    //     return `${format(new Date(params.value[0]), REGULAR_DATE_FORMAT)}<br/>Status: ${
    //       healthStatusMap[params.value[1]] ?? 'N/A'
    //     }`;
    //   },
    // },
  };

  useEffect(() => {
    const chartDataRaw = getChartData();
    setChartData(chartDataRaw);
  }, [diagnostics, selectedComponent]);

  useEffect(() => {
    const baseSeries = {
      type: 'custom',
      name: 'Health Status',
      renderItem: (params: any, api: any) => {
        const x = api.coord([api.value(0), 0])[0];
        const y = api.coord([api.value(0), 0])[1];
        return {
          type: 'circle',
          shape: { cx: x, cy: y, r: 6 },
          style: {
            fill: healthStatusNoShadowColorMap[api.value(1)] || '#ccc',
            stroke: '#fff',
            lineWidth: 2,
          },
        };
      },
      encode: { x: 0, y: 1 },
      data: chartData.map((item: any) => [item.value[0], item.value[1]]),
      z: 10,
    };
    const activeSeries =
      activeChartPoint != null
        ? {
            type: 'custom',
            name: 'Active Highlight',
            renderItem: (params: any, api: any) => {
              const x = api.coord([api.value(0), 0])[0];
              const y = api.coord([api.value(0), 0])[1];
              return {
                type: 'circle',
                shape: { cx: x, cy: y, r: 4 },
                style: {
                  fill: 'transparent',
                  stroke: healthStatusToShadowColorMap[api.value(1)] || '#ccc',
                  lineWidth: 20,
                },
                silent: true,
              };
            },
            encode: { x: 0, y: 1 },
            data: [[activeChartPoint[0], activeChartPoint[1]]],
            z: 5,
          }
        : null;

    setChartOptions({
      ...commonOptions,
      toolbox,
      xAxis: {
        ...commonOptions.xAxis,
        type: isMaintenanceChartVisible.onDiagnostics ? 'time' : 'category',
        min: chartData[0]?.value?.[0],
        max: chartData.length === 1 ? chartData[0]?.value?.[0] : undefined,
        splitNumber: chartData.length === 1 ? 1 : undefined,
        minInterval: chartData.length === 1 ? 3600 * 24 * 1000 : undefined,
      },
      dataZoom: [{ type: 'inside', filterMode: 'none', ...dataZoom }],
      series: [baseSeries, ...(activeSeries ? [activeSeries] : [])],
    });
  }, [chartData, activeChartPoint, isMaintenanceChartVisible.onDiagnostics]);

  useEffect(() => {
    if (chartData.length) {
      const latest = chartData[chartData.length - 1].value;
      const found = chartData.some((item: any) => item.name === format(new Date(activeChartPoint?.[0]), 'yyyy-MM-dd'));
      onPointClick(found ? activeChartPoint : latest);
    }
  }, [chartData]);

  useEffect(() => {
    const instance = diagnosticsChartRef.current?.getEchartsInstance() as echarts.ECharts;
    if (instance) {
      instance.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: isZoomActive,
      });
    }
  }, [isZoomActive]);

  useEffect(() => {
    setIsZoomActive(false);
  }, [isMaintenanceChartVisible.onDiagnostics]);

  const onEventsHandlers = {
    click: (e: any) => {
      onPointClick(e.data);
      setIsZoomActive(false);
    },
    datazoom: (e: any) => {
      const batch = e.batch?.[0];
      if (batch) {
        const updated: Record<string, number> = {};
        if (batch.start != null && batch.end != null) {
          updated.start = batch.start;
          updated.end = batch.end;
        }
        if (batch.startValue != null && batch.endValue != null) {
          updated.startValue = batch.startValue;
          updated.endValue = batch.endValue;
        }
        setDataZoom(updated);
      }
    },
    finished: () => {
      const m = maintenanceChartRef.current?.getEchartsInstance() as echarts.ECharts;
      const d = diagnosticsChartRef.current?.getEchartsInstance() as echarts.ECharts;
      if (m && d) {
        echarts.connect([m, d]);
        d.on('restore', () => {
          d.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
          setDataZoom({ start: 0, end: 100 });
        });
      }
    },
    restore: () => setDataZoom({ start: 0, end: 100 }),
  };

  const fusionTrendChartElement = useMemo(
    () => (
      <ReactECharts
        ref={diagnosticsChartRef}
        className="fusion-trend-chart"
        echarts={echarts}
        option={chartOptions}
        onEvents={onEventsHandlers}
        style={{ height: '80px', minHeight: '80px' }}
        notMerge={true}
        lazyUpdate={true}
      />
    ),
    [selectedComponent, chartOptions]
  );

  if (!showMaintenance) return fusionTrendChartElement;

  return (
    <div ref={wrapperRef} className={'flex flex-col w-full min-h-[120px] flex-1'}>
      {fusionTrendChartElement}
      {isMaintenanceChartVisible.onDiagnostics && (
        <MaintenanceChart
          selectedComponent={selectedComponent}
          xAxisMin={from}
          xAxisMax={to}
          context={MAINTENANCE_CHART_CONTEXT.Diagnostics}
          wrapperRef={wrapperRef}
          diagnosticsChartRef={diagnosticsChartRef}
          maintenanceChartRef={maintenanceChartRef}
        />
      )}

      {showMaintenance && (
        <div
          className={`
      h-[34px] pt-3
      ${isMaintenanceChartVisible.onDiagnostics ? 'absolute' : 'static'}
      bottom-4 right-4
      flex items-center justify-end gap-[20px] pr-8
    `}
        >
          <Icon
            icon={PlusCircleIcon}
            className={`w-6 h-6 hover:text-[#7071f3] cursor-pointer ${isZoomActive ? '!text-[#7071f3]' : ''}`}
            onClick={() => {
              setIsZoomActive((prev) => !prev);
            }}
          />
          <Icon
            icon={RefreshIcon}
            className="w-6 h-6 hover:text-[#7071f3] cursor-pointer"
            onClick={() => {
              const instance = diagnosticsChartRef.current?.getEchartsInstance() as echarts.ECharts;
              setIsZoomActive(false);
              instance?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
              setDataZoom({ start: 0, end: 100 });
            }}
          />
        </div>
      )}
    </div>
  );
};